import {defineStore} from 'pinia'
import axios from '@/api/axios'
import {isSearchFeedsCompany} from '@/views/companies/isSearchFeedsCompany'

export const useCompanyStore = defineStore({
  id: 'companyStore',
  state: () => ({
    companyChangeTrigger: false,
    companies: [],
    companiesLoading: false,
    companyToEditLoading: false,
    error: null,
    showCompanyModal: false,
    companyToEditHash: null,
    companyToEdit: null,
    companyTab: null,
    types: ['Solo Affiliate', 'Media Buying Team', 'Direct Advertiser / Product Owner', 'Agency'],
  }),
  getters: {
    companiesOptions: state => {
      return state.companies.map(({hash, title}) => ({value: hash, label: title}))
    },
    companyTypesOptions: state => {
      return state.types.map(item => ({value: item, label: item}))
    },
    externalCompaniesOptions: state => {
      return state.companies
        .filter(n => n.is_internal === 0)
        .map(({hash, title}) => ({
          value: hash,
          label: title,
        }))
    },
    companiesWithEmptyOptions: state => {
      let result = state.companies.map(({hash, title}) => ({value: hash, label: title}))

      result.unshift({value: 0, label: 'No company'})

      return result
    },
    internalCompaniesOptions: state => {
      return state.companies
        .filter(n => n.is_internal === 1)
        .map(company => ({
          value: company.hash,
          label: company.title,
          is_sf: isSearchFeedsCompany(company),
        }))
    },
    verticals: () => {
      return [
        'App - Antivirus',
        'App - Utility',
        'App - VPN',
        'Comics',
        'Content Arbitrage',
        'Crypto',
        'Dating - AI',
        'Dating - Adult',
        'Dating - Mainstream',
        'Debt Consolidation',
        'Debt Relief',
        'Delivery services',
        'E-commerce',
        'Education',
        'Employment',
        'Finance',
        'Forex',
        'Gaming',
        'Health & Beauty',
        'Home Services',
        'Horoscopes',
        'Insurance - Auto',
        'Insurance - Health',
        'Insurance - Home',
        'Insurance - Life',
        'Loans - PayDay',
        'Loans - Personal',
        'Medicare',
        'Nutra',
        'Real Estate',
        'Search Arbitrage',
        'SOI Sweepstakes',
        'Social Casino',
        'Streaming services',
        'Taxi',
        'Trading',
        'Travel',
        'Web Utilities',
      ]
    },
    verticalOptions: getters => {
      return getters.verticals.map(item => ({value: item, label: item}))
    },
  },
  actions: {
    async getCompanies(payload = {}, changeCompaniesGlobally = true) {
      this.error = null
      this.companiesLoading = true

      const {signal, ...params} = payload

      try {
        const {data} = await axios.get('companies', {
          params,
          ...(signal && {signal}),
        })

        if (changeCompaniesGlobally) {
          this.companies = data
        }

        return data
      } catch (responseError) {
        this.error = responseError?.data
      } finally {
        this.companiesLoading = false
      }
    },
    async getCompaniesMinimal(changeCompaniesGlobally = true, params = {}) {
      this.error = null
      this.companiesLoading = true

      try {
        const {data} = await axios.get('v1/companies', {
          params: {...params, resource: 'minimal'},
        })

        if (changeCompaniesGlobally) {
          this.companies = data
        }

        return data
      } catch (responseError) {
        this.error = responseError?.data
      } finally {
        this.companiesLoading = false
      }
    },
    async createCompany(payload) {
      const {data} = await axios.post('companies', payload)

      return data
    },
    async updateCompany(payload, hash) {
      await axios.put(`companies/${hash}`, payload)

      return true
    },
    async patchCompany(hash, payload) {
      await axios.patch(`companies/${hash}`, payload)

      return true
    },
    async deleteCompany(hash) {
      await axios.delete(`companies/${hash}`)

      return true
    },
    async patchCompanySettings(hash, payload) {
      await axios.patch(`companies/${hash}/settings`, payload)

      return true
    },

    async getCompanyTags() {
      const {data} = await axios.get('company-tags')

      return data
    },

    async getCompanySpendStats(hash) {
      const {data} = await axios.get(`/companies/${hash}/spend-stats`)

      return data
    },

    async getCreditLineOverview(hash) {
      const {data} = await axios.get(`/companies/${hash}/credit-line-overview`)

      return data
    },

    async getCompanyToEdit(company_hash) {
      this.companyToEditLoading = true
      try {
        let {data} = await axios.get(`companies/${company_hash}`, {
          params: {
            with: [
              'sale_manager',
              'media_planner',
              'media_planner_2',
              'csm',
              'csm_2',
              'tags',
              'verticals',
              'first_user',
              'meta',
            ],
            with_count: [
              'users',
              'quick_books_customers',
              'documents',
              'estimate_invoices',
              'payments',
              'overdraft_requests',
              'retainers',
              'statuses_history',
            ],
          },
        })

        data.balances_diff = Math.abs(data.qb_balance) - data.balance

        this.companyToEdit = data

        return data
      } catch (error) {
        //
      } finally {
        this.companyToEditLoading = false
      }
    },

    async getCompanyByHash(company_hash) {
      try {
        let {data} = await axios.get(`companies/${company_hash}`, {
          params: {
            with: [
              'sale_manager',
              'media_planner',
              'media_planner_2',
              'csm',
              'csm_2',
              'tags',
              'verticals',
              'first_user',
              'meta',
            ],
            with_count: [
              'users',
              'quick_books_customers',
              'documents',
              'estimate_invoices',
              'payments',
              'overdraft_requests',
              'retainers',
            ],
          },
        })

        data.balances_diff = Math.abs(data.qb_balance) - data.balance

        return data
      } catch (error) {
        //
      }
    },

    async getCompanyForRetainerSign(company_hash) {
      const {data} = await axios.get(`companies/${company_hash}`, {
        params: {
          with: [
            'sale_manager',
            'meta',
            'questionnaire',
            'commission',
            'quick_books_customers',
            'default_quick_books_customer',
            'retainers',
          ],
        },
      })

      return data
    },

    async getCompanyByHashPublic(company_hash, params = {}) {
      const {data} = await axios.get(`public/companies/${company_hash}`, {params})

      return data
    },
    async getStatusesHistory(companyHash) {
      const {data} = await axios.get(`companies/${companyHash}/status-history`)

      return data
    },
    async openCompanyModal(company_hash = null, tab) {
      this.companyToEditHash = company_hash

      this.showCompanyModal = true
      this.companyTab = tab
    },

    formatCompanyPayload(companyToEdit, isBusinessUnitHomeIQ) {
      let payload = cloneSafe(companyToEdit)
      payload.tags = payload.tags.map(i => i.name)

      if (!payload.additional_languages) {
        payload.additional_languages = []
      }

      if (!isBusinessUnitHomeIQ) {
        payload.csm_hash = companyToEdit.csm?.hash ?? null
        payload.csm_2_hash = companyToEdit.csm_2?.hash ?? null
        payload.media_planner_hash = companyToEdit.media_planner?.hash ?? null
        payload.media_planner_2_hash = companyToEdit.media_planner_2?.hash ?? null

        payload.sale_manager_hash = companyToEdit.sale_manager?.hash ?? null
      }

      if (isBusinessUnitHomeIQ || companyToEdit?.business_unit === 'home_iq') {
        payload.meta === null ? (payload.meta = {}) : ''
      }

      return payload
    },

    resetCompanyToEdit() {
      this.companyToEdit = null
    },
    resetCompanyToEditHash() {
      this.companyToEditHash = null
    },

    // Counters
    incrementQuickBooksCustomerCount() {
      this.companyToEdit.quick_books_customers_count += 1
    },

    decreaseQuickBooksCustomerCount() {
      this.companyToEdit.quick_books_customers_count -= 1
    },

    incrementUserCount() {
      this.companyToEdit.users_count += 1
    },

    incrementInvoicesCount() {
      this.companyToEdit.estimate_invoices_count += 1
    },

    incrementPaymentsCount() {
      this.companyToEdit.payments_count += 1
    },

    incrementDocumentsCount() {
      this.companyToEdit.documents_count += 1
    },

    setRetainersCount(count) {
      if (!this.companyToEdit) {
        return
      }

      this.companyToEdit.retainers_count = count
    },
    toggleCompanyChangeTrigger() {
      this.companyChangeTrigger = !this.companyChangeTrigger
    },
  },
})
